<template>
  <Transition name="player-slide">
    <div
      v-if="currentTrack"
      class="audio-player"
      :class="{ 'audio-player--mini': isMobile && !isExpanded }"
    >
      <!-- Primary audio element (can become secondary during crossfade swap) -->
      <!-- NOTE: src is managed via watcher, NOT via binding, to prevent crossfade reset -->
      <audio
        ref="audioRef"
        preload="metadata"
        @timeupdate="!useSecondaryAudio && onTimeUpdate()"
        @loadedmetadata="!useSecondaryAudio && onLoadedMetadata()"
        @loadeddata="!useSecondaryAudio ? onLoadedData() : onNextTrackLoaded()"
        @ended="!useSecondaryAudio && onEnded()"
        @error="!useSecondaryAudio ? onError($event) : onNextTrackError()"
        @play="!useSecondaryAudio && onPlay()"
        @pause="!useSecondaryAudio && onPause()"
      />
      <!-- Secondary audio element for crossfade (can become primary after swap) -->
      <!-- NOTE: src is managed via watcher, NOT via binding, to prevent crossfade reset -->
      <audio
        ref="nextAudioRef"
        preload="metadata"
        @timeupdate="useSecondaryAudio && onTimeUpdate()"
        @loadedmetadata="useSecondaryAudio && onLoadedMetadata()"
        @loadeddata="useSecondaryAudio ? onLoadedData() : onNextTrackLoaded()"
        @ended="useSecondaryAudio && onEnded()"
        @error="useSecondaryAudio ? onError($event) : onNextTrackError()"
        @play="useSecondaryAudio && onPlay()"
        @pause="useSecondaryAudio && onPause()"
      />

      <!-- Container wrapper - constrains all player content to match app-container (max 1360px centered) -->
      <div class="audio-player__container">
        <!-- Mini Player for Mobile: Art left, Title + Controls stacked, Expand top-right -->
      <div
        v-if="isMobile && !isExpanded"
        class="audio-player__mini"
      >
        <!-- Left: Artwork -->
        <img
          v-if="currentTrack.images?.[0]"
          :src="(currentTrack.images[0] as any).url"
          :alt="currentTrack.title"
          class="audio-player__mini-artwork"
        />
        <div v-else class="audio-player__mini-artwork-placeholder">
          <v-icon size="24" color="grey">mdi-music-note</v-icon>
        </div>

        <!-- Center: Title + Controls stacked -->
        <div class="audio-player__mini-center">
          <!-- Row 1: Title (2 lines max) -->
          <div class="audio-player__mini-title">{{ currentTrack.title }}</div>

          <!-- Row 2: Playback Controls + Close -->
          <div class="audio-player__mini-controls" @click.stop>
            <PlaybackControls
              size="mini"
              :show-shuffle="!isRadioStationMode"
              :show-repeat="!isRadioStationMode"
              :disable-previous="isAtFirstRadioTrack"
              :disable-next="!hasNext"
              :play-button-variant="isPlaying ? 'flat' : 'text'"
              play-button-color="primary"
              icon-color="primary"
            />
            <v-btn
              icon
              variant="text"
              size="x-small"
              class="audio-player__mini-btn"
              @click.stop="close"
            >
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Expand button (circular tab at top center, halfway out) -->
        <button
          class="audio-player__mini-expand"
          aria-label="Open full player"
          @click.stop="toggleExpanded"
        >
          <v-icon size="18">mdi-chevron-up</v-icon>
        </button>
      </div>

      <!-- Full Player (desktop or mobile expanded) -->
      <template v-if="!isMobile || isExpanded">
        <!-- Mobile Header (Close + Rating Countdown) - Desktop player only -->
        <div v-if="isMobile" class="audio-player__mobile-header">
          <!-- Star Rating / Countdown (centered) -->
          <div class="audio-player__mobile-rating">
            <RatingSection
              v-if="!isWsomOwned || userRating !== null"
              :listen-time-seconds="listenTime"
              :readonly="isWsomOwned && userRating !== null"
              :disabled="isRatingLoading"
              :wsom-entry-ulid="wsomEntryUlid"
              compact
              @rate="handleRate"
              @remove="handleRemoveRating"
            />
          </div>

          <!-- Close Button (top right) -->
          <v-btn
            icon
            variant="text"
            size="small"
            class="audio-player__mobile-close"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Content wrapper - aligns with app-container layout (sidebar + content area) -->
        <div class="audio-player__content">
          <!-- Track Info -->
          <TrackInfo
            :track="currentTrack"
            size="compact"
            :clickable="true"
            class="audio-player__track"
            @click="navigateToSongDetail"
            @artwork-click="navigateToSongDetail"
          />

          <!-- Controls -->
          <PlaybackControls
            size="compact"
            :show-shuffle="!isRadioStationMode"
            :show-repeat="!isRadioStationMode"
            :disable-previous="isAtFirstRadioTrack"
            :disable-next="!hasNext"
            icon-color="primary"
            class="audio-player__controls"
          />

          <!-- Progress -->
          <ProgressBar
            layout="inline"
            :color="'primary'"
            class="audio-player__progress"
            @seek-start="onSeekStart"
            @seek-end="onSeekEnd"
          />

          <!-- Volume & Actions -->
          <div class="audio-player__actions">
          <!-- Star Rating (Desktop only - Mobile has it in header) -->
          <div
            v-if="!isMobile"
            class="audio-player__rating-wrapper"
          >
            <RatingSection
              v-if="!isWsomOwned || userRating !== null"
              :listen-time-seconds="listenTime"
              :readonly="isWsomOwned && userRating !== null"
              :disabled="isRatingLoading"
              :wsom-entry-ulid="wsomEntryUlid"
              compact
              @rate="handleRate"
              @remove="handleRemoveRating"
            />
          </div>

          <!-- Volume Controls (Desktop only - mobile uses device volume) -->
          <!-- Inline slider for wide screens -->
          <VolumeControl
            v-if="!isMobile"
            direction="horizontal"
            :show-slider="true"
            slider-width="100%"
            class="audio-player__volume audio-player__volume--inline"
          />
          <!-- Popup slider for narrow desktop screens -->
          <VolumeControl
            v-if="!isMobile"
            :popup-mode="true"
            class="audio-player__volume audio-player__volume--popup"
          />

          <!-- Queue Button -->
          <!-- Hide queue panel button in radio station mode -->
          <v-btn
            v-if="!isRadioStationMode"
            icon
            variant="text"
            size="small"
            :color="queuePanelOpen ? 'primary' : undefined"
            @click="toggleQueuePanel"
            aria-label="Toggle queue"
          >
            <v-icon>mdi-playlist-play</v-icon>
            <v-badge
              v-if="queue.length > 1"
              :content="queue.length"
              color="primary"
              floating
              offset-x="-4"
              offset-y="-4"
            />
          </v-btn>

          <!-- Expand to Full Player Button (Desktop only) -->
          <v-btn
            v-if="!isMobile"
            icon
            variant="text"
            size="small"
            aria-label="Open full player"
            @click="isExpanded = true"
          >
            <v-icon>mdi-arrow-expand</v-icon>
          </v-btn>

          <!-- Close Button (Desktop only - Mobile has it in header) -->
          <v-btn
            v-if="!isMobile"
            icon
            variant="text"
            size="small"
            class="audio-player__desktop-close"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <!-- Mobile Close Button (CSS shows only at mobile widths) -->
          <v-btn
            icon
            variant="text"
            size="small"
            class="audio-player__mobile-close-btn"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          </div>

          <!-- Mobile Expand Button (CSS shows only at mobile widths) -->
          <button
            class="audio-player__mobile-expand"
            aria-label="Open full player"
            @click.stop="isExpanded = true"
          >
            <v-icon size="18">mdi-chevron-up</v-icon>
          </button>
        </div>
      </template>
      </div><!-- End .audio-player__container -->
    </div>
  </Transition>

  <!-- Full Screen Player (Mobile and Desktop) -->
  <FullScreenPlayer v-model="isExpanded" />

  <!-- Queue Panel (lazy-mounted) -->
  <QueuePanel
    v-if="queuePanelOpen"
    v-model="queuePanelOpen"
    :queue="queue"
    :current-track="currentTrack"
    :queue-index="queueIndex"
    :is-playing="isPlaying"
    @remove="handleRemoveFromQueue"
    @play="handlePlayAtIndex"
    @toggle-play="handleTogglePlayFromQueue"
    @clear="handleClearQueue"
  />
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
  defineAsyncComponent,
} from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useSdk } from "@/composables/useSdk";
import { useAudioPlayer } from "@/composables/useAudioPlayer";
import { useAudioViewTracking } from "@/composables/useAudioViewTracking";
import { useAuth } from "@/composables/useAuth";
import { useHlsAudio } from "@/composables/useHlsAudio";
import StarRating from "./StarRating.vue";
import FullScreenPlayer from "./FullScreenPlayer.vue";
import PlaybackControls from "./controls/PlaybackControls.vue";
import ProgressBar from "./controls/ProgressBar.vue";
import VolumeControl from "./controls/VolumeControl.vue";
import TrackInfo from "./controls/TrackInfo.vue";
import RatingSection from "./controls/RatingSection.vue";
import type { Post } from "@/types";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";

const QueuePanel = defineAsyncComponent(() => import("./QueuePanel.vue"));

// Component loaded successfully

const { sdk } = useSdk();
const audioPlayer = useAudioPlayer();
const router = useRouter();
const { mobile } = useDisplay();

// Initialize HLS audio for both primary and secondary audio elements
// Each audio element needs its own HLS instance for crossfade support
const primaryHls = useHlsAudio();
const secondaryHls = useHlsAudio();

// Track whether we're using HLS or direct MP3 for fallback
const usingHlsForPrimary = ref(false);
const usingHlsForSecondary = ref(false);
const hlsFallbackTrigger = ref(0); // Increment to trigger MP3 fallback

// Initialize audio view tracking
useAudioViewTracking();
const isTouchDevice = computed(() => {
  // Prefer a stable, device-capability signal over breakpoints.
  // Pixel devices in particular can end up in non-"mobile" breakpoints depending on viewport.
  if (typeof navigator === "undefined") return false;
  return (navigator.maxTouchPoints ?? 0) > 0;
});

// Only show mini player on actual mobile devices (touch + small screen)
// Not on small desktop windows
const isMobile = computed(() => mobile.value && isTouchDevice.value);

// Player state
const audioRef = ref<HTMLAudioElement | null>(null);
const nextAudioRef = ref<HTMLAudioElement | null>(null); // For crossfade
const adAudioRef = ref<HTMLAudioElement | null>(null); // Dedicated audio element for ads

// Crossfade configuration constants
const CROSSFADE_FADE_INTERVAL_MS = 50; // Update volume every 50ms for smooth transition
const CROSSFADE_PRELOAD_SECONDS = 10; // Start preloading when 10 seconds remain
const MIN_TRACK_LENGTH_FOR_CROSSFADE_SECONDS = 8; // Don't crossfade tracks shorter than this

// Crossfade state
const crossfadeIntervalId = ref<number | null>(null);
const nextTrackPreloaded = ref(false);
const crossfadeStarted = ref(false);
const useSecondaryAudio = ref(false); // Flag to track which audio element is primary
const crossfadeCompleting = ref(false); // Flag to prevent template src updates during crossfade completion

// Computed refs to get current primary/secondary audio elements based on flag
const primaryAudio = computed(() =>
  useSecondaryAudio.value ? nextAudioRef.value : audioRef.value,
);
const secondaryAudio = computed(() =>
  useSecondaryAudio.value ? audioRef.value : nextAudioRef.value,
);

const {
  currentTrack,
  queue,
  queueIndex,
  isPlaying,
  currentTime,
  duration,
  volume,
  isMuted,
  queuePanelOpen,
  pendingAutoPlay,
  isShuffleEnabled,
  repeatMode,
    // Crossfade state
    isCrossfading,
    isRadioStationMode,
    isAtFirstRadioTrack,
    CROSSFADE_DURATION,
    CROSSFADE_START_OFFSET,
} = audioPlayer;

// Guard seeking so scroll gestures can't accidentally drag the slider.
const isSeeking = ref(false);

// User profile cache for artist name
const cachedUserProfile = ref<UserProfile | null>(null);
const isLoadingProfile = ref(false);

// Rating state
const userRating = ref<number | null>(null);
const isRatingLoading = ref(false);
const listenTime = ref(0); // Track how long user has listened to current song
const previousTimeRef = ref(0); // Track previous time for listen calculation
const RATING_THRESHOLD_SECONDS = 15; // Must listen 15 seconds before rating

// Mobile player expansion state
const isExpanded = ref(false); // Mobile full-screen player expansion state

// Audio media interface
interface AudioMedia {
  url?: string;
  hlsUrl?: string;
  duration?: number;
  thumbnail?: string;
  type?: string;
}

// Helper to get post property
function getTrackProp<T>(key: string): T | undefined {
  if (!currentTrack.value) return undefined;
  return (currentTrack.value as Record<string, unknown>)[key] as T | undefined;
}

// Get user ID from current track
const trackUserId = computed(() => {
  if (!currentTrack.value) return undefined;

  if (currentTrack.value.userId) {
    return currentTrack.value.userId;
  }

  const userId =
    getTrackProp<string>("user_ulid") || getTrackProp<string>("userULID");

  const userObj = getTrackProp<{ ulid?: string; userId?: string }>("user");

  return userId || userObj?.ulid || userObj?.userId;
});

// Fetch user profile for artist name
async function fetchUserProfile() {
  const userId = trackUserId.value;

  if (!userId || !sdk) {
    isLoadingProfile.value = false;
    return;
  }

  isLoadingProfile.value = true;

  try {
    const profile = await sdk.fetchUserProfileById(userId);
    if (profile) {
      cachedUserProfile.value = profile;
    }
  } catch (error) {
    console.error("[AudioPlayer] Failed to fetch user profile:", error);
  } finally {
    isLoadingProfile.value = false;
  }
}

// Load user's rating for current track
function loadUserRating() {
  if (!currentTrack.value) {
    userRating.value = null;
    return;
  }

  const trackUlid = currentTrack.value.ulid || currentTrack.value.id;

  // Check the shared rating cache first (may have been set by SongDetailView)
  if (trackUlid) {
    const cache = audioPlayer.ratingCache;
    if (cache?.value) {
      const cachedRating = cache.value.get(trackUlid) || 0;
      if (cachedRating > 0) {
        userRating.value = cachedRating;
        return;
      }
    }
  }

  // Fall back to the track data
  const rating = getTrackProp<number>("userRating");
  userRating.value = rating ?? null;
}

// Helper to update shared rating cache
function updateRatingCache(ulid: string, rating: number) {
  const cache = audioPlayer.ratingCache;
  if (cache?.value) {
    if (rating === 0) {
      cache.value.delete(ulid);
    } else {
      cache.value.set(ulid, rating);
    }
    // Trigger reactivity by creating a new Map
    cache.value = new Map(cache.value);
  }
}

// Handle rating a song
async function handleRate(rating: number) {
  if (!currentTrack.value || !sdk) return;

  const trackUlid = currentTrack.value.ulid || currentTrack.value.id;
  if (!trackUlid) return;

  // Check if this is a WSOM entry (has wsomEntryUlid property)
  const wsomEntryUlid = (currentTrack.value as any).wsomEntryUlid;

  isRatingLoading.value = true;
  try {
    if (wsomEntryUlid) {
      // Use WSOM rating endpoint for WSOM entries
      console.log("[AudioPlayer] Rating WSOM entry:", {
        wsomEntryUlid,
        rating,
      });
      const { wsomService } = await import("@/services/wsomService");
      await wsomService.rateEntry(wsomEntryUlid, rating);
    } else {
      // Use standard post rating endpoint for regular posts
      await sdk.ratePost(trackUlid, rating);
    }

    userRating.value = rating;

    // Update shared rating cache so other components (SongDetailView) can see the change
    updateRatingCache(trackUlid, rating);

    // If zero rating and there are more songs in the queue, skip to the next song
    if (rating === 0 && queueIndex.value < queue.value.length - 1) {
      audioPlayer.skipNext();
    }
  } catch (error) {
    console.error("Failed to rate song:", error);
    // Revert on failure
    loadUserRating();
  } finally {
    isRatingLoading.value = false;
  }
}

// Handle removing a rating
async function handleRemoveRating() {
  if (!currentTrack.value || !sdk) return;

  const trackUlid = currentTrack.value.ulid || currentTrack.value.id;
  if (!trackUlid) return;

  isRatingLoading.value = true;
  try {
    await sdk.removeRating(trackUlid);
    userRating.value = null;

    // Update shared rating cache (0 = no rating)
    updateRatingCache(trackUlid, 0);
  } catch (error) {
    console.error("Failed to remove rating:", error);
    // Revert on failure
    loadUserRating();
  } finally {
    isRatingLoading.value = false;
  }
}

// Computed
const audioUrl = computed(() => {
  if (!currentTrack.value) return null;

  // Check streamUrl first (radio streams)
  if (currentTrack.value.streamUrl) {
    return currentTrack.value.streamUrl;
  }

  // Check audio array (try both 'audio' and 'audios')
  const audioArr =
    getTrackProp<AudioMedia[]>("audio") || getTrackProp<AudioMedia[]>("audios");
  if (audioArr && audioArr.length > 0 && audioArr[0].url) {
    return audioArr[0].url;
  }

  // Check attachments for audio type
  const attachments = getTrackProp<AudioMedia[]>("attachments");
  if (attachments) {
    const audioAttachment = attachments.find(
      (a) => a?.type === "audio" && a.url,
    );
    if (audioAttachment?.url) {
      return audioAttachment.url;
    }
  }

  // Try various URL fields
  const audioUrlField = getTrackProp<string>("audioUrl");
  if (audioUrlField) return audioUrlField;

  const audioSrc = getTrackProp<string>("audioSrc");
  if (audioSrc) return audioSrc;

  return null;
});

// Computed HLS URL - check for adaptive streaming URL in audio array
const hlsUrl = computed(() => {
  // Never use HLS after a fallback was triggered (until track changes)
  if (hlsFallbackTrigger.value > 0) return null;

  if (!currentTrack.value) return null;

  // Radio streams don't use HLS (they have their own streaming format)
  if (currentTrack.value.streamUrl) return null;

  // Check audio array for hlsUrl
  const audioArr =
    getTrackProp<AudioMedia[]>("audio") || getTrackProp<AudioMedia[]>("audios");
  if (audioArr && audioArr.length > 0 && audioArr[0].hlsUrl) {
    return audioArr[0].hlsUrl;
  }

  // Check attachments for audio with hlsUrl
  const attachments = getTrackProp<AudioMedia[]>("attachments");
  if (attachments) {
    const audioAttachment = attachments.find(
      (a) => a?.type === "audio" && a.hlsUrl,
    );
    if (audioAttachment?.hlsUrl) {
      return audioAttachment.hlsUrl;
    }
  }

  return null;
});

// Reset HLS fallback trigger when track changes
watch(currentTrack, () => {
  hlsFallbackTrigger.value = 0;
});

/**
 * Attach HLS to an audio element, with fallback to MP3 on error.
 * Returns true if HLS was attached, false if direct src should be used.
 */
function attachHlsToElement(
  audio: HTMLAudioElement,
  hlsUrlValue: string,
  mp3Url: string | null,
  hlsComposable: ReturnType<typeof useHlsAudio>,
  isSecondary: boolean
): boolean {
  const success = hlsComposable.attachHls(audio, hlsUrlValue, (error) => {
    // HLS fatal error - fallback to MP3
    console.warn("[AudioPlayer] HLS fatal error, falling back to MP3:", error);
    hlsComposable.detachHls();
    if (isSecondary) {
      usingHlsForSecondary.value = false;
    } else {
      usingHlsForPrimary.value = false;
    }
    // Increment fallback trigger to prevent HLS retry for this track
    hlsFallbackTrigger.value++;
    // Set direct MP3 source
    if (mp3Url) {
      audio.src = mp3Url;
      audio.load();
    }
  });

  if (success) {
    if (isSecondary) {
      usingHlsForSecondary.value = true;
    } else {
      usingHlsForPrimary.value = true;
    }
  }

  return success;
}

/**
 * Detach HLS from audio elements when cleaning up.
 */
function detachHlsFromElement(
  hlsComposable: ReturnType<typeof useHlsAudio>,
  isSecondary: boolean
): void {
  hlsComposable.detachHls();
  if (isSecondary) {
    usingHlsForSecondary.value = false;
  } else {
    usingHlsForPrimary.value = false;
  }
}

// Watcher to update primary audio element src when audioUrl changes
// This replaces Vue binding to give us full control during crossfade
// Supports HLS adaptive bitrate streaming with fallback to direct MP3
// NOTE: crossfadeCompleting is NOT in the watch array - we only check its value
// inside the watcher. If it were watched, the watcher would re-run when crossfade
// completes, which would reset the src and stop the already-playing audio.
watch(
  [audioUrl, hlsUrl, useSecondaryAudio, hlsFallbackTrigger],
  async ([newAudioUrl, newHlsUrl, isSecondaryPrimary, _fallbackTrigger]) => {
    // Check crossfadeCompleting inside the watcher, not as a watched dependency
    const isCompleting = crossfadeCompleting.value;

    console.log("[AudioPlayer:watcher] audioUrl changed", {
      hasUrl: !!newAudioUrl,
      hasHlsUrl: !!newHlsUrl,
      urlPreview: newAudioUrl ? newAudioUrl.substring(0, 60) + "..." : null,
      hlsPreview: newHlsUrl ? newHlsUrl.substring(0, 60) + "..." : null,
      isSecondaryPrimary,
      isCompleting,
      pendingAutoPlay: pendingAutoPlay.value,
    });

    // Skip src updates during crossfade completion - the audio is already playing
    if (isCompleting) {
      console.log("[AudioPlayer:watcher] Skipping - crossfade completing");
      return;
    }

    // Wait for next tick to ensure refs are available
    console.log("[AudioPlayer:watcher] Awaiting nextTick (THIS BREAKS MOBILE GESTURE)");
    await nextTick();

    const primary = isSecondaryPrimary ? nextAudioRef.value : audioRef.value;
    const hlsComposable = isSecondaryPrimary ? secondaryHls : primaryHls;

    console.log("[AudioPlayer:watcher] After nextTick", {
      hasPrimary: !!primary,
      primarySrc: primary?.src ? primary.src.substring(0, 60) + "..." : null,
    });

    if (primary && (newAudioUrl || newHlsUrl)) {
      // Detach any existing HLS instance before setting new source
      detachHlsFromElement(hlsComposable, isSecondaryPrimary);

      let sourceSet = false;

      // Try HLS first if available and supported
      if (newHlsUrl && (primaryHls.isHlsSupported || hasNativeHlsSupport(primary))) {
        console.log("[AudioPlayer:watcher] Attempting HLS playback");
        sourceSet = attachHlsToElement(
          primary,
          newHlsUrl,
          newAudioUrl,
          hlsComposable,
          isSecondaryPrimary
        );
        if (sourceSet) {
          console.log("[AudioPlayer:watcher] HLS attached successfully");
        }
      }

      // Fall back to direct MP3 if HLS not available or attachment failed
      if (!sourceSet && newAudioUrl) {
        // Only update if src is different to avoid resetting playback
        const srcChanged = primary.src !== newAudioUrl;
        if (srcChanged) {
          console.log("[AudioPlayer:watcher] Setting direct MP3 src on primary");
          primary.src = newAudioUrl;
          // Load the audio to trigger metadata loading
          primary.load();
        }
        sourceSet = true;
      }

      // If we have pendingAutoPlay and source is now set, trigger play
      // This handles the case where the player wasn't mounted when play was requested
      if (pendingAutoPlay.value && currentTrack.value && sourceSet) {
        console.log("[AudioPlayer:watcher] pendingAutoPlay=true, waiting for canplay...");
        // Wait for audio to be ready
        await new Promise<void>((resolve) => {
          const onCanPlay = () => {
            primary.removeEventListener('canplay', onCanPlay);
            console.log("[AudioPlayer:watcher] canplay event received");
            resolve();
          };
          // If already can play, resolve immediately
          if (primary.readyState >= 2) {
            console.log("[AudioPlayer:watcher] Already can play (readyState >= 2)");
            resolve();
          } else {
            primary.addEventListener('canplay', onCanPlay);
            // Timeout after 2 seconds to prevent infinite waiting
            setTimeout(() => {
              primary.removeEventListener('canplay', onCanPlay);
              console.log("[AudioPlayer:watcher] canplay timeout after 2s");
              resolve();
            }, 2000);
          }
        });

        // Set volume before playing
        primary.volume = volume.value / 100;
        pendingAutoPlay.value = false;
        console.log("[AudioPlayer:watcher] Calling primary.play() (ASYNC PATH - may fail on mobile)");
        try {
          await primary.play();
          console.log("[AudioPlayer:watcher] primary.play() SUCCESS");
          // onPlay event handler will set isPlaying.value = true
        } catch (err) {
          console.error("[AudioPlayer:watcher] primary.play() FAILED:", err);
          isPlaying.value = false;
        }
      } else if (!sourceSet) {
        console.log("[AudioPlayer:watcher] No source set - no HLS or MP3 URL available");
      } else {
        console.log("[AudioPlayer:watcher] Not auto-playing", {
          pendingAutoPlay: pendingAutoPlay.value,
          hasCurrentTrack: !!currentTrack.value,
        });
      }
    }
  },
  { immediate: true }
);

/**
 * Check if browser has native HLS support (Safari, iOS).
 */
function hasNativeHlsSupport(audio: HTMLAudioElement): boolean {
  return audio.canPlayType("application/vnd.apple.mpegurl") !== "";
}

const artworkUrl = computed(() => {
  if (!currentTrack.value) return null;
  const images = currentTrack.value.images as
    | Array<{ url?: string }>
    | undefined;
  return images?.[0]?.url || null;
});

/**
 * Get audio URL for any track (used for crossfade preloading)
 */
function getAudioUrlForTrack(track: Post | null): string | null {
  if (!track) return null;

  // Check streamUrl first (radio streams - no crossfade for these)
  if (track.streamUrl) return null;

  // Check audio array
  const audioArr =
    ((track as Record<string, unknown>).audio as AudioMedia[] | undefined) ||
    ((track as Record<string, unknown>).audios as AudioMedia[] | undefined);
  if (audioArr && audioArr.length > 0 && audioArr[0].url) {
    return audioArr[0].url;
  }

  // Check attachments for audio type
  const attachments = (track as Record<string, unknown>).attachments as
    | AudioMedia[]
    | undefined;
  if (attachments) {
    const audioAttachment = attachments.find(
      (a) => a?.type === "audio" && a.url,
    );
    if (audioAttachment?.url) {
      return audioAttachment.url;
    }
  }

  // Try various URL fields
  const audioUrlField = (track as Record<string, unknown>).audioUrl as
    | string
    | undefined;
  if (audioUrlField) return audioUrlField;

  const audioSrc = (track as Record<string, unknown>).audioSrc as
    | string
    | undefined;
  if (audioSrc) return audioSrc;

  return null;
}

/**
 * Get HLS URL for any track (used for crossfade preloading)
 */
function getHlsUrlForTrack(track: Post | null): string | null {
  if (!track) return null;

  // Radio streams don't use HLS (they have their own streaming format)
  if (track.streamUrl) return null;

  // Check audio array for hlsUrl
  const audioArr =
    ((track as Record<string, unknown>).audio as AudioMedia[] | undefined) ||
    ((track as Record<string, unknown>).audios as AudioMedia[] | undefined);
  if (audioArr && audioArr.length > 0 && audioArr[0].hlsUrl) {
    return audioArr[0].hlsUrl;
  }

  // Check attachments for audio with hlsUrl
  const attachments = (track as Record<string, unknown>).attachments as
    | AudioMedia[]
    | undefined;
  if (attachments) {
    const audioAttachment = attachments.find(
      (a) => a?.type === "audio" && a.hlsUrl,
    );
    if (audioAttachment?.hlsUrl) {
      return audioAttachment.hlsUrl;
    }
  }

  return null;
}

// Artist - check multiple possible fields
const displayArtist = computed(() => {
  if (!currentTrack.value) return "Unknown Artist";

  // Check direct artist field
  if (currentTrack.value.artist) return currentTrack.value.artist;

  // Check alternative field names
  const artistName =
    getTrackProp<string>("artist_name") || getTrackProp<string>("artistName");
  if (artistName) return artistName;

  // Check embedded user data for name
  // Prefer displayName over name for consistency with QueueItem
  const user = getTrackProp<{
    name?: string;
    displayName?: string;
  }>("user");
  if (user) {
    if (user.displayName) return user.displayName;
    if (user.name) return user.name;
  }

  // Check flat user fields
  const userName =
    getTrackProp<string>("userName") || getTrackProp<string>("user_name");
  if (userName) return userName;

  // Show loading state while fetching profile (before checking cached profile)
  if (isLoadingProfile.value) return "Loading...";

  // Check cached user profile (fetched via SDK)
  // Prefer displayName over name for consistency with QueueItem
  if (cachedUserProfile.value) {
    const profile = cachedUserProfile.value;
    if (profile.displayName) return profile.displayName;
    const profileName = (profile as Record<string, unknown>).name as
      | string
      | undefined;
    if (profileName) return profileName;
  }

  return "Unknown Artist";
});

const hasPrevious = computed(() => {
  // Allow back navigation while repeating (wrap or restart) even when at index 0
  if (repeatMode.value !== "off") {
    return queue.value.length > 0;
  }
  return queueIndex.value > 0;
});
const hasNext = computed(() => {
  // Always allow next if repeat mode is enabled
  if (repeatMode.value !== "off") return true;
  // Otherwise, only allow if not at end of queue
  return queueIndex.value < queue.value.length - 1;
});

const progressPercent = computed(() => {
  if (!duration.value || duration.value <= 0) return 0;
  const percent = (currentTime.value / duration.value) * 100;
  return Math.max(0, Math.min(100, percent));
});

// Get track user username to check if wsom-owned
const trackUserUsername = computed(() => {
  if (!currentTrack.value) return null;
  
  // Check embedded user object
  const user = getTrackProp<{ username?: string }>("user");
  if (user?.username) return user.username;
  
  // Check cached user profile
  if (cachedUserProfile.value) {
    const profile = cachedUserProfile.value as Record<string, unknown>;
    const username = profile.username as string | undefined;
    if (username) return username;
  }
  
  // Check flat username field
  const username = getTrackProp<string>("userUsername") || getTrackProp<string>("user_username");
  if (username) return username;
  
  return null;
});

// Check if post is owned by WSOM (World Series of Music)
const isWsomOwned = computed(() => {
  return trackUserUsername.value === "wsom";
});

// Get WSOM entry ULID if this is a WSOM entry
const wsomEntryUlid = computed(() => {
  if (!currentTrack.value) return undefined;
  return (currentTrack.value as Record<string, unknown>).wsomEntryUlid as string | undefined;
});

// Rating is enabled after listening for 15 seconds OR if user already rated
const canRate = computed(() => {
  return (
    listenTime.value >= RATING_THRESHOLD_SECONDS || userRating.value !== null
  );
});

// Countdown timer for rating unlock
const secondsUntilCanRate = computed(() => {
  if (userRating.value !== null) return 0; // Already rated
  const remaining = RATING_THRESHOLD_SECONDS - listenTime.value;
  return Math.max(0, Math.ceil(remaining));
});

// Animation flag when rating unlocks
const ratingJustUnlocked = ref(false);

const volumeIcon = computed(() => {
  if (isMuted.value || volume.value === 0) return "mdi-volume-off";
  if (volume.value < 50) return "mdi-volume-medium";
  return "mdi-volume-high";
});

const repeatIcon = computed(() => {
  if (repeatMode.value === "one") return "mdi-repeat-once";
  if (repeatMode.value === "all") return "mdi-repeat";
  return "mdi-repeat-off";
});

// Methods
function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// ============================================================================
// CROSSFADE FUNCTIONS
// ============================================================================

/**
 * Preload the next track into the secondary audio element
 * Supports HLS adaptive bitrate streaming with fallback to direct MP3
 */
function preloadNextTrack() {
  if (nextTrackPreloaded.value || crossfadeStarted.value) return;

  const nextTrack = audioPlayer.getNextTrack();
  if (!nextTrack) return;

  const nextUrl = getAudioUrlForTrack(nextTrack);
  const nextHlsUrl = getHlsUrlForTrack(nextTrack);
  const secondary = secondaryAudio.value;
  if ((!nextUrl && !nextHlsUrl) || !secondary) return;

  // Detach any existing HLS from secondary element
  const isSecondary = !useSecondaryAudio.value; // Secondary is the non-primary
  const hlsComposable = isSecondary ? secondaryHls : primaryHls;
  detachHlsFromElement(hlsComposable, isSecondary);

  // Set volume to 0 for preloading (crossfade will adjust)
  secondary.volume = 0;

  // Try HLS first if available
  if (nextHlsUrl && (primaryHls.isHlsSupported || hasNativeHlsSupport(secondary))) {
    console.log("[AudioPlayer:preload] Preloading next track with HLS");
    const success = attachHlsToElement(
      secondary,
      nextHlsUrl,
      nextUrl,
      hlsComposable,
      isSecondary
    );
    if (success) {
      // HLS will handle loading
      return;
    }
  }

  // Fall back to direct MP3
  if (nextUrl) {
    console.log("[AudioPlayer:preload] Preloading next track with direct MP3");
    secondary.src = nextUrl;
    secondary.load();
  }
}

/**
 * Handler for when next track is loaded and ready
 */
function onNextTrackLoaded() {
  nextTrackPreloaded.value = true;
}

/**
 * Handler for errors loading next track
 */
function onNextTrackError() {
  nextTrackPreloaded.value = false;
  // Continue without crossfade if next track fails to load
}

/**
 * Clear the preloaded next track from secondary audio element
 * Used when an ad becomes pending and we don't want crossfade to occur
 */
function clearPreloadedNextTrack() {
  const secondary = secondaryAudio.value;
  if (secondary) {
    // Detach HLS from secondary element if active
    const isSecondary = !useSecondaryAudio.value;
    const hlsComposable = isSecondary ? secondaryHls : primaryHls;
    detachHlsFromElement(hlsComposable, isSecondary);

    secondary.pause();
    secondary.currentTime = 0;
    secondary.src = "";
  }
  nextTrackPreloaded.value = false;
}

/**
 * Start the crossfade transition
 */
function startCrossfade() {
  if (crossfadeStarted.value || !nextTrackPreloaded.value) return;
  const primary = primaryAudio.value;
  const secondary = secondaryAudio.value;
  if (!primary || !secondary) return;

  crossfadeStarted.value = true;
  audioPlayer.setCrossfading(true);

  // Clear any existing interval first to prevent leaks
  if (crossfadeIntervalId.value !== null) {
    clearInterval(crossfadeIntervalId.value);
    crossfadeIntervalId.value = null;
  }

  // Start playing the next track at 0 volume
  secondary.volume = 0;
  secondary.play().catch((err) => {
    console.error("[AudioPlayer] Failed to start crossfade playback:", err);
    cancelCrossfade();
  });

  const startTime = Date.now();

  crossfadeIntervalId.value = window.setInterval(() => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(1, elapsed / CROSSFADE_DURATION);

    audioPlayer.setCrossfadeProgress(progress);

    // Use ease-in-out curve for smoother transition
    const easedProgress =
      progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

    // Get current volume to respond to volume changes during fade
    const currentVolume = volume.value / 100;

    // Fade out current track (primary)
    const primaryEl = primaryAudio.value;
    if (primaryEl) {
      primaryEl.volume = currentVolume * (1 - easedProgress);
    }

    // Fade in next track (secondary)
    const secondaryEl = secondaryAudio.value;
    if (secondaryEl) {
      secondaryEl.volume = currentVolume * easedProgress;
    }

    // Crossfade complete
    if (progress >= 1) {
      completeCrossfade();
    }
  }, CROSSFADE_FADE_INTERVAL_MS);
}

/**
 * Complete the crossfade and transition to the next track
 */
function completeCrossfade() {
  // Guard against double-completion
  if (crossfadeIntervalId.value === null && !crossfadeStarted.value) {
    return;
  }

  if (crossfadeIntervalId.value !== null) {
    clearInterval(crossfadeIntervalId.value);
    crossfadeIntervalId.value = null;
  }

  // Capture references BEFORE swapping
  const oldPrimary = primaryAudio.value;
  const newPrimary = secondaryAudio.value;

  // Set the new primary track volume to full BEFORE swapping
  if (newPrimary) {
    newPrimary.volume = volume.value / 100;
  }

  // Set flag to prevent src watcher from updating during crossfade completion
  // MUST be set BEFORE toggling useSecondaryAudio
  crossfadeCompleting.value = true;

  // Toggle the flag - this swaps which element is considered primary
  // This ensures that when we pause the old primary, its onPause handler
  // guard (!useSecondaryAudio or useSecondaryAudio) will NOT pass,
  // preventing isPlaying from being set to false
  useSecondaryAudio.value = !useSecondaryAudio.value;

  // NOW stop the old track (which is now considered secondary due to the flag swap)
  // Its pause event won't trigger onPause() because the guard check will fail
  if (oldPrimary) {
    oldPrimary.pause();
    oldPrimary.currentTime = 0;
    oldPrimary.src = ""; // Clear src so it's ready for next preload
  }

  // Update the composable's reference to the new primary element
  audioPlayer.setAudioElement(primaryAudio.value);
  audioPlayer.setNextAudioElement(secondaryAudio.value);

  // Reset crossfade state BEFORE skipNext to avoid race condition
  crossfadeStarted.value = false;
  nextTrackPreloaded.value = false;
  audioPlayer.setCrossfading(false);

  // Move to the next track in queue
  // Note: This updates currentTrack which will trigger the watcher
  // but we DON'T want it to auto-play since we already have audio playing.
  // Pass true (auto-advance) so the skip is NOT reported as a user skip.
  audioPlayer.skipNext(true);
  pendingAutoPlay.value = false;

  // Clear the completing flag after a short delay to allow Vue to settle
  // The watcher will have run by then
  setTimeout(() => {
    crossfadeCompleting.value = false;

    // Sync duration from the new primary audio element (which was preloaded)
    const newPrimaryNow = primaryAudio.value;
    if (newPrimaryNow && newPrimaryNow.duration && !isNaN(newPrimaryNow.duration)) {
      duration.value = newPrimaryNow.duration;
    }
  }, 100);
}

/**
 * Cancel any ongoing crossfade
 */
function cancelCrossfade() {
  if (crossfadeIntervalId.value !== null) {
    clearInterval(crossfadeIntervalId.value);
    crossfadeIntervalId.value = null;
  }

  // Stop the secondary track if it was playing (the one being faded in)
  const secondary = secondaryAudio.value;
  if (secondary) {
    secondary.pause();
    secondary.currentTime = 0;
    secondary.src = "";
  }

  // Restore primary track volume
  const primary = primaryAudio.value;
  if (primary) {
    primary.volume = volume.value / 100;
  }

  crossfadeStarted.value = false;
  nextTrackPreloaded.value = false;
  audioPlayer.setCrossfading(false);
}

/**
 * Check if we should preload or start crossfade based on current playback position
 */
function checkCrossfadeTiming() {
  const primary = primaryAudio.value;
  if (!primary || !duration.value || duration.value <= 0) return;
  if (currentTrack.value?.streamUrl) return; // No crossfade for streams

  const timeRemaining = duration.value - currentTime.value;
  const nextTrack = audioPlayer.getNextTrack();

  // Don't crossfade if no next track
  if (!nextTrack) return;

  // Don't crossfade for very short tracks
  if (duration.value < MIN_TRACK_LENGTH_FOR_CROSSFADE_SECONDS) return;

  // Preload when approaching end of track
  if (
    timeRemaining <= CROSSFADE_PRELOAD_SECONDS &&
    !nextTrackPreloaded.value &&
    !crossfadeStarted.value
  ) {
    preloadNextTrack();
  }

  // Start crossfade when we're within the offset time
  if (
    timeRemaining <= CROSSFADE_START_OFFSET &&
    nextTrackPreloaded.value &&
    !crossfadeStarted.value
  ) {
    startCrossfade();
  }
}

function play(post: Post, playQueue?: Post[]) {
  // pendingAutoPlay is now handled by the composable
  audioPlayer.play(post, playQueue);
}

async function togglePlay() {
  const primary = primaryAudio.value;
  if (!primary) {
    console.warn("Audio element not available");
    return;
  }

  // Let the audio element's play/pause events handle state updates
  // This prevents race conditions between manual state management and event handlers
  if (isPlaying.value) {
    primary.pause();
    // onPause event handler will set isPlaying.value = false
    return;
  }

  // On mobile devices (especially iOS), ensure audio is ready before playing
  // Check if src is set and audio has loaded enough data
  if (!primary.src || primary.src === '') {
    console.warn("Audio src not set, cannot play");
    isPlaying.value = false;
    return;
  }

  // Ensure audio has loaded enough data to play (especially important on mobile)
  // readyState: 0 = HAVE_NOTHING, 1 = HAVE_METADATA, 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA
  if (primary.readyState < 2) {
    console.log("Audio not ready yet, loading...");
    // Preserve the current playback position before reloading
    // (load() resets currentTime to 0)
    const savedTime = currentTime.value;

    // Load the audio and wait for it to be ready
    primary.load();

    // Wait for audio to be ready (with timeout)
    try {
      await new Promise<void>((resolve, reject) => {
        const onCanPlay = () => {
          primary.removeEventListener('canplay', onCanPlay);
          primary.removeEventListener('error', onError);
          resolve();
        };
        const onError = (e: Event) => {
          primary.removeEventListener('canplay', onCanPlay);
          primary.removeEventListener('error', onError);
          reject(e);
        };

        if (primary.readyState >= 2) {
          resolve();
        } else {
          primary.addEventListener('canplay', onCanPlay);
          primary.addEventListener('error', onError);
          // Timeout after 3 seconds
          setTimeout(() => {
            primary.removeEventListener('canplay', onCanPlay);
            primary.removeEventListener('error', onError);
            reject(new Error('Audio load timeout'));
          }, 3000);
        }
      });

      // Restore the playback position after audio is ready
      if (savedTime > 0 && isFinite(savedTime)) {
        primary.currentTime = savedTime;
        currentTime.value = savedTime;
      }
    } catch (loadErr) {
      console.error("Failed to load audio:", loadErr);
      isPlaying.value = false;
      return;
    }
  }

  try {
    await primary.play();
    // onPlay event handler will set isPlaying.value = true
  } catch (err) {
    console.error("Audio play failed:", err);
    // On error, manually set to false since play event won't fire
    isPlaying.value = false;
    
    // On mobile, provide more detailed error information
    if (err instanceof Error) {
      console.error("Play error details:", {
        message: err.message,
        name: err.name,
        readyState: primary.readyState,
        src: primary.src,
        networkState: primary.networkState,
        error: primary.error
      });
    }
  }
}

function playPrevious() {
  cancelCrossfade(); // Cancel any ongoing crossfade

  // Standard audio player behavior:
  // - If more than 3 seconds into the song, restart from beginning
  // - If within first 3 seconds and there's a previous track, go to previous
  const RESTART_THRESHOLD = 3;

  if (currentTime.value > RESTART_THRESHOLD) {
    // Restart current song from beginning
    const primary = primaryAudio.value;
    if (primary) {
      primary.currentTime = 0;
      currentTime.value = 0;
    }
    return;
  }

  // Within first 3 seconds - go to previous track if available
  if (!hasPrevious.value) {
    // No previous track, just restart current song
    const primary = primaryAudio.value;
    if (primary) {
      primary.currentTime = 0;
      currentTime.value = 0;
    }
    return;
  }

  // Go to previous track
  audioPlayer.playPrevious();
}

function playNext() {
  cancelCrossfade(); // Cancel any ongoing crossfade
  // Skip the check - let the composable handle repeat logic
  // pendingAutoPlay is now handled by the composable
  audioPlayer.skipNext();
}

function onSeek(value: number) {
  // Only allow seeking while the user is actively dragging the slider.
  // This prevents "global swipe" / scrolling from moving the slider and causing skips.
  if (isTouchDevice.value && !isSeeking.value) return;
  const primary = primaryAudio.value;
  if (!primary || !duration.value) return;
  const time = (value / 100) * duration.value;
  primary.currentTime = time;
  currentTime.value = time;
}

function onSeekStart() {
  isSeeking.value = true;
}

function onSeekEnd() {
  isSeeking.value = false;
}

function onVolumeChange(value: number | number[]) {
  const nextVolume = Array.isArray(value) ? value[0] : value;
  volume.value = nextVolume;
  isMuted.value = nextVolume === 0;
  const primary = primaryAudio.value;
  if (primary) {
    primary.volume = nextVolume / 100;
  }
}

function toggleMute() {
  audioPlayer.toggleMute();
  const primary = primaryAudio.value;
  if (primary) primary.volume = volume.value / 100;
}

function toggleShuffle() {
  audioPlayer.toggleShuffle();
}

function toggleRepeat() {
  audioPlayer.toggleRepeat();
}

function close() {
  cancelCrossfade(); // Cancel any ongoing song crossfade
  audioPlayer.stop();
  audioPlayer.updateMediaSessionPlaybackState("none");
  audioPlayer.stopPositionTracking();
  queuePanelOpen.value = false;
  isExpanded.value = false; // Collapse mobile player when closing
}

// Toggle mobile player expansion
function toggleExpanded() {
  if (!isMobile.value) return; // Only for mobile
  isExpanded.value = !isExpanded.value;
}

// Collapse mobile player (for swipe down gesture or close button in expanded view)
function collapsePlayer() {
  isExpanded.value = false;
}

// Navigate to song detail page
function navigateToSongDetail() {
  if (!currentTrack.value) return;
  const ulid = currentTrack.value.ulid || currentTrack.value.id;
  if (ulid) {
    router.push(`/song/${ulid}`);
  }
}

// Queue panel handlers
function toggleQueuePanel() {
  audioPlayer.toggleQueuePanel();
}

function handleRemoveFromQueue(index: number) {
  if (index < 0 || index >= queue.value.length) return;
  const wasPlaying = isPlaying.value;
  const removingCurrent = index === queueIndex.value;
  pendingAutoPlay.value = wasPlaying && removingCurrent;
  audioPlayer.removeFromQueue(index);
}

function handlePlayAtIndex(index: number) {
  if (index < 0 || index >= queue.value.length) return;
  pendingAutoPlay.value = true;
  audioPlayer.playAtIndex(index);
}

function handleClearQueue() {
  close();
}

function handleTogglePlayFromQueue() {
  audioPlayer.togglePlay();
}

// Event handlers
function onTimeUpdate() {
  const primary = primaryAudio.value;
  if (!primary) return;
  const newTime = primary.currentTime;

  // Ensure we have a valid time value
  if (isFinite(newTime) && newTime >= 0) {
    currentTime.value = newTime;
  }

  // Track listen time (only count forward progress, not seeks)
  if (
    isPlaying.value &&
    newTime > previousTimeRef.value &&
    newTime - previousTimeRef.value < 2
  ) {
    listenTime.value += newTime - previousTimeRef.value;
  }
  previousTimeRef.value = newTime;

  // Update Media Session position (throttled internally)
  audioPlayer.updateMediaSessionPosition();

  // Check if we should preload/start crossfade
  checkCrossfadeTiming();
}

function onLoadedMetadata() {
  const primary = primaryAudio.value;
  if (!primary) return;
  const newDuration = primary.duration;

  // Update duration if we have a valid value
  // Prefer the larger/more accurate value if both are valid
  if (isFinite(newDuration) && newDuration > 0) {
    // Only update if we don't have a duration yet, or if the new value is larger (more accurate)
    if (duration.value === 0 || newDuration > duration.value) {
      duration.value = newDuration;
    }
  } else if (duration.value === 0) {
    // Only set to 0 if we don't have a valid duration yet
    duration.value = 0;
  }

  primary.volume = volume.value / 100;
}

function onLoadedData() {
  // Ensure duration is set when data is loaded
  // This can fire before or after loadedmetadata, so we need to handle both cases
  const primary = primaryAudio.value;
  if (!primary) return;
  const newDuration = primary.duration;

  // Update duration if we have a valid value
  // Prefer the larger/more accurate value if both are valid
  if (isFinite(newDuration) && newDuration > 0) {
    // Only update if we don't have a duration yet, or if the new value is larger (more accurate)
    if (duration.value === 0 || newDuration > duration.value) {
      duration.value = newDuration;
    }
  }
}

async function onEnded() {
  // If crossfade is in progress, ignore the ended event
  // The crossfade logic handles the transition
  if (crossfadeStarted.value || isCrossfading.value) {
    return;
  }

  if (hasNext.value) {
    // Continue playing next track - pass true to force auto-play
    // since isPlaying is false when the song ends
    audioPlayer.skipNext(true);
  } else {
    // Track ended and no next track - audio element is paused
    // Set state to match the actual audio element state
    isPlaying.value = false;
    currentTime.value = 0;
  }
}

function onError(e: Event) {
  console.error("Audio playback error:", e);
  isPlaying.value = false;
}

function onPlay() {
  isPlaying.value = true;
  audioPlayer.updateMediaSessionPlaybackState("playing");
  audioPlayer.updateMediaSession(displayArtist.value); // Update metadata when playback starts
  audioPlayer.startPositionTracking();
}

function onPause() {
  isPlaying.value = false;
  audioPlayer.updateMediaSessionPlaybackState("paused");
  audioPlayer.stopPositionTracking();
}

// Store previous userId to detect if we need to refetch profile
let previousUserId: string | undefined;

// Watch for track changes
watch(
  currentTrack,
  async (newTrack) => {
    // Cancel any ongoing crossfade if track changes unexpectedly
    // (e.g., user manually skips during crossfade)
    if (crossfadeStarted.value && !isCrossfading.value) {
      cancelCrossfade();
    }

    // Reset crossfade preload state for new track
    nextTrackPreloaded.value = false;

    const primary = primaryAudio.value;

    if (!newTrack) {
      // Reset state when track is cleared
      cancelCrossfade(); // Also cancel crossfade when stopping
      if (primary) {
        primary.pause();
        // onPause event handler will set isPlaying.value = false
        // But we also set it here for immediate feedback since track is being cleared
        isPlaying.value = false;
      }
      currentTime.value = 0;
      duration.value = 0;
      userRating.value = null;
      listenTime.value = 0;
      isLoadingProfile.value = false;
      cachedUserProfile.value = null;

      // Clear Media Session
      audioPlayer.updateMediaSessionPlaybackState("none");
      audioPlayer.stopPositionTracking();
      return;
    }

    // Fetch profile FIRST (doesn't need primary audio)
    const currentUserId = trackUserId.value;
    if (currentUserId !== previousUserId) {
      cachedUserProfile.value = null;
      fetchUserProfile();
      previousUserId = currentUserId;
    }

    // Load user rating (doesn't need primary audio either)
    loadUserRating();

    // Reset per-track state
    currentTime.value = 0;
    duration.value = 0;
    listenTime.value = 0;
    previousTimeRef.value = 0;

    // Exit if primary audio not ready (for audio playback only)
    if (!primary) {
      return;
    }

    // Ensure <audio src> has updated before playing
    await nextTick();

    // Keep volume in sync
    primary.volume = volume.value / 100;

    // Update Media Session metadata for new track (after primary audio is ready)
    audioPlayer.updateMediaSession(displayArtist.value);

    if (pendingAutoPlay.value) {
      // Check if src is already set and matches audioUrl
      // If not, the audioUrl watcher will handle playing when src is set
      if (primary.src && primary.src === audioUrl.value) {
        // Src is ready, try to play now
        pendingAutoPlay.value = false;
        try {
          await primary.play();
          // onPlay event handler will set isPlaying.value = true
        } catch (err) {
          console.error("Auto-play failed:", err);
          // On error, manually set to false since play event won't fire
          isPlaying.value = false;
          // Ensure audio element is paused
          if (!primary.paused) {
            primary.pause();
          }
        }
      } else if (audioUrl.value) {
        // Src needs to be set - ensure it's set and wait for it
        if (primary.src !== audioUrl.value) {
          primary.src = audioUrl.value;
          primary.load();
        }
        // Wait for audio to be ready
        await new Promise<void>((resolve) => {
          const onCanPlay = () => {
            primary.removeEventListener('canplay', onCanPlay);
            resolve();
          };
          if (primary.readyState >= 2) {
            resolve();
          } else {
            primary.addEventListener('canplay', onCanPlay);
            setTimeout(() => {
              primary.removeEventListener('canplay', onCanPlay);
              resolve();
            }, 2000);
          }
        });
        
        // Now try to play
        if (pendingAutoPlay.value) {
          pendingAutoPlay.value = false;
          try {
            await primary.play();
            // onPlay event handler will set isPlaying.value = true
          } catch (err) {
            console.error("Auto-play failed:", err);
            isPlaying.value = false;
            if (!primary.paused) {
              primary.pause();
            }
          }
        }
      }
      // If audioUrl is not available yet, keep pendingAutoPlay true
      // The audioUrl watcher will trigger play when it becomes available
    } else {
      // Sync state with actual audio element state
      // This prevents stuck state if something went wrong
      // BUT: Don't interfere during crossfade operations - they manage their own state
      if (!crossfadeCompleting.value) {
        if (primary.paused && isPlaying.value) {
          // Audio is paused but state says playing - fix it
          isPlaying.value = false;
        } else if (!primary.paused && !isPlaying.value) {
          // Audio is playing but state says paused - sync it
          // This can happen after crossfade completion
          isPlaying.value = true;
        }
      }
    }
  },
  { immediate: true },
);

// Keyboard shortcuts
function handleKeyboard(e: KeyboardEvent) {
  if (!currentTrack.value) return;

  // Skip if focus is in an editable element (input, textarea, content-editable)
  const target = e.target as HTMLElement;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable ||
    target.closest('[contenteditable="true"]')
  )
    return;

  // Skip if a modal/dialog is open (e.g., compose post modal)
  if (document.querySelector('.compose-modal-overlay, [role="dialog"][aria-modal="true"]'))
    return;

  const primary = primaryAudio.value;

  switch (e.code) {
    case "Space":
      e.preventDefault();
      togglePlay();
      break;
    case "ArrowLeft":
      if (primary) {
        primary.currentTime = Math.max(0, primary.currentTime - 10);
      }
      break;
    case "ArrowRight":
      if (primary) {
        primary.currentTime = Math.min(
          duration.value,
          primary.currentTime + 10,
        );
      }
      break;
  }
}

// Watch for audio elements becoming available and register them with global composable
// We use primaryAudio computed to always get the current primary element
watch(
  [audioRef, nextAudioRef, useSecondaryAudio],
  async () => {
    const primary = primaryAudio.value;
    const secondary = secondaryAudio.value;

    audioPlayer.setAudioElement(primary);
    audioPlayer.setNextAudioElement(secondary);

    // Setup Media Session API when audio element becomes available
    if (primary) {
      audioPlayer.setupMediaSession();
    }

    // If audio element just became available and there's a pending track to auto-play,
    // trigger playback (handles case where player wasn't mounted on first click)
    if (
      primary &&
      currentTrack.value &&
      pendingAutoPlay.value &&
      audioUrl.value
    ) {
      await nextTick();
      // Ensure src is set before trying to play
      // The audioUrl watcher sets src, but we need to wait for it
      if (primary.src !== audioUrl.value) {
        primary.src = audioUrl.value;
        primary.load();
        // Wait for the audio to be ready before playing
        await new Promise<void>((resolve) => {
          const onCanPlay = () => {
            primary.removeEventListener('canplay', onCanPlay);
            resolve();
          };
          primary.addEventListener('canplay', onCanPlay);
          // Timeout after 2 seconds to prevent infinite waiting
          setTimeout(() => {
            primary.removeEventListener('canplay', onCanPlay);
            resolve();
          }, 2000);
        });
      }
      // Set volume before playing
      primary.volume = volume.value / 100;
      pendingAutoPlay.value = false;
      try {
        await primary.play();
        // onPlay event handler will set isPlaying.value = true
      } catch (err) {
        console.error("Auto-play failed after mount:", err);
        isPlaying.value = false;
      }
    }
  },
  { immediate: true },
);

// Watch for rating unlock and trigger animation (only for unrated songs)
watch(canRate, (newVal, oldVal) => {
  // Only show unlock animation if:
  // 1. Rating just became available (newVal && !oldVal)
  // 2. User hasn't rated this song yet (userRating.value === null)
  if (newVal && !oldVal && userRating.value === null) {
    ratingJustUnlocked.value = true;
    // Reset animation flag after animation completes
    setTimeout(() => {
      ratingJustUnlocked.value = false;
    }, 1000);
  }
});

// Update Media Session when artist name changes (fetched asynchronously)
watch(displayArtist, (newArtist) => {
  if (
    currentTrack.value &&
    newArtist !== "Loading..." &&
    newArtist !== "Unknown Artist"
  ) {
    audioPlayer.updateMediaSession(newArtist);
  }
});

onMounted(() => {
  window.addEventListener("keydown", handleKeyboard);
  // Ensure we never stay "stuck" in seeking mode on mobile.
  // (Android scroll can cancel pointer sequences without firing pointerup on the slider.)
  window.addEventListener("pointerup", onSeekEnd, { passive: true });
  window.addEventListener("pointercancel", onSeekEnd, { passive: true });
  window.addEventListener("blur", onSeekEnd, { passive: true });
  window.addEventListener("scroll", handleScrollCancelSeek, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyboard);
  window.removeEventListener("pointerup", onSeekEnd);
  window.removeEventListener("pointercancel", onSeekEnd);
  window.removeEventListener("blur", onSeekEnd);
  window.removeEventListener("scroll", handleScrollCancelSeek);

  // Clean up crossfades
  cancelCrossfade();

  // Clean up HLS instances
  primaryHls.detachHls();
  secondaryHls.detachHls();

  // Clean up audio element registration
  audioPlayer.setAudioElement(null);
  audioPlayer.setNextAudioElement(null);
});

function handleScrollCancelSeek() {
  if (isSeeking.value) onSeekEnd();
}

// Expose methods for parent components
defineExpose({
  play,
  togglePlay,
  playNext,
  playPrevious,
  close,
  isPlaying,
  currentTrack,
});
</script>

<style lang="scss">
// Non-scoped styles for teleported component
// The AudioPlayer is teleported to body, outside .v-application
// The container wrapper constrains all player content to match app-container
// Using the same CSS variables defined in App.vue for consistency

// Container wrapper - groups all player content
.audio-player__container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
}

// Content inside the container - takes remaining space after sidebar offset
.audio-player__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  height: 100%;
}
</style>

<style scoped lang="scss">
.audio-player {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  width: 100%;
  max-width: var(--app-max-width, 1360px);
  height: 88px;
  box-sizing: border-box;
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  border-radius: 0;

  // Container and content styles are in non-scoped section (for teleported component)

  // Mini player mode for mobile - compact bar above bottom nav
  &--mini {
    position: fixed !important;
    // Position directly above bottom tab bar (56px height + safe area)
    bottom: calc(56px + env(safe-area-inset-bottom, 0px) + var(--mobile-browser-chrome-height, 0px)) !important;
    left: 0 !important;
    right: 0 !important;
    width: 100vw !important;
    max-width: none !important;
    transform: none !important;
    height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
    z-index: 1001 !important;
    background: rgba(var(--v-theme-surface), 0.98) !important;
    backdrop-filter: blur(20px) !important;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15) !important;
  }

  // Mini player: Art left, Title + Controls center, Expand top-right
  &__mini {
    width: 100% !important;
    margin: 0 !important;
    padding: 8px 12px !important;
    cursor: pointer;
    transition: background 0.2s ease;
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.05) !important;
    }
  }

  &__mini-artwork {
    width: 56px;
    height: 56px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__mini-artwork-placeholder {
    width: 56px;
    height: 56px;
    border-radius: 6px;
    background: rgba(var(--v-theme-on-surface), 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  // Center column: title + controls stacked
  &__mini-center {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding-right: 32px; // Space for expand button
  }

  &__mini-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
  }

  &__mini-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0;
  }

  // Expand button - circular tab at top right, halfway outside player
  &__mini-expand {
    position: absolute;
    top: -16px; // Half of 32px height to sit halfway out
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.15);
    background: rgba(var(--v-theme-surface), 0.95);
    color: rgba(var(--v-theme-on-surface), 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
    transition: background 0.2s ease, color 0.2s ease;

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__mini-artist {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }

  // Mini player buttons - ensure 44px minimum touch targets
  &__mini-btn {
    width: 44px !important;
    height: 44px !important;
    min-width: 44px !important;
    flex-shrink: 0;

    &--play {
      width: 48px !important;
      height: 48px !important;
      min-width: 48px !important;
    }
  }

  // Mobile header with close button and rating countdown
  &__mobile-header {
    position: absolute;
    top: 8px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    z-index: 10;
  }

  &__mobile-rating {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__mobile-close {
    position: absolute;
    right: 8px;
    top: 0;
  }

  &__track {
    display: flex;
    align-items: center;
    gap: 12px;
    width: auto;
    flex: 0 1 280px;
    min-width: 160px;
    height: 56px;
  }

  &__artwork {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 6px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--clickable {
      cursor: pointer;
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      &:focus-visible {
        outline: 2px solid rgb(var(--v-theme-primary));
        outline-offset: 2px;
      }
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    height: 56px;
    gap: 4px;
  }

  &__title {
    font-family: var(--font-display);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;

    &--clickable {
      cursor: pointer;
      transition: color 0.15s ease;

      &:hover {
        color: rgb(var(--v-theme-primary));
      }

      &:focus-visible {
        outline: 2px solid rgb(var(--v-theme-primary));
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }

  &__artist {
    font-family: var(--font-body);
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }

  // Ad-specific styles
  &__ad-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 2;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.05em;
  }

  &__ad-artwork {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-warning), 0.15),
      rgba(var(--v-theme-warning), 0.05)
    );
  }

  &__ad-title {
    color: rgb(var(--v-theme-warning-darken-1));
  }

  &__ad-sponsor {
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-style: italic;
  }

  &__slider--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__slider--admin-radio {
    // Red progress bar for admin in radio mode
    opacity: 1;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 4px;
    width: auto;
    flex: 0 1 180px;
    min-width: 140px;
    height: 56px;
    flex-shrink: 1;
  }

  &__progress {
    flex: 1 1 224px; // 280px * 0.8
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 128px; // 160px * 0.8
    max-width: 384px; // 480px * 0.8
    height: 56px;
    // Allow vertical scrolling gestures even if the finger lands on the player.
    // We keep seeking disabled on mobile via :readonly + onSeek guard.
    touch-action: pan-y;
  }

  &__slider {
    flex: 1;
    touch-action: pan-y;
  }

  &__time {
    font-family: var(--font-body);
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-variant-numeric: tabular-nums;
    min-width: 32px;
    text-align: center;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    width: auto;
    flex: 0 1 260px;
    min-width: 160px;
    height: 56px;
    flex-shrink: 1;
  }

  &__rating-wrapper {
    display: flex;
    align-items: center;
    margin-right: 8px;
    padding-right: 8px;
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  }

  &__rating {
    &--locked {
      opacity: 0.4;
    }
  }

  // Mobile expand button - hidden by default, shown via media query
  &__mobile-expand {
    display: none; // Hidden on desktop
    position: absolute;
    top: -16px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.15);
    background: rgba(var(--v-theme-surface), 0.95);
    color: rgba(var(--v-theme-on-surface), 0.7);
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
    transition: background 0.2s ease, color 0.2s ease;
    z-index: 10;

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  // Mobile close button - hidden by default, shown via media query
  &__mobile-close-btn {
    display: none !important; // Hidden on desktop
  }

  &__volume {
    width: 120px;
    max-width: 120px;
    min-width: 100px;
    flex-shrink: 0;
  }
}

// Compact single-row layout for desktop widths <= 1700px
@media (max-width: 1700px) {
  .audio-player {
    gap: 8px;
    padding: 8px 16px;
    height: 56px;
  }

  .audio-player__content {
    gap: 8px;
  }

  // Compact buttons in controls and actions
  .audio-player__controls,
  .audio-player__actions {
    flex: 0 0 auto;
    min-width: auto;
    gap: 2px;

    :deep(.v-btn) {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }
  }

  .audio-player__track {
    flex: 0 1 auto;
    min-width: 140px;
    max-width: 280px;
    height: 40px;
    gap: 8px;
  }

  .audio-player__artwork {
    width: 40px;
    height: 40px;
    border-radius: 4px;
  }

  .audio-player__info {
    height: 40px;
    gap: 2px;
  }

  .audio-player__title {
    font-size: 0.8125rem;
  }

  .audio-player__artist {
    font-size: 0.6875rem;
  }

  .audio-player__progress {
    flex: 1 1 auto;
    min-width: 100px;
  }

  .audio-player__time {
    min-width: 28px;
    font-size: 0.6875rem;
  }

  .audio-player__volume {
    flex-shrink: 0;
  }

  .audio-player__volume--inline {
    display: flex;
    width: 100px;
    min-width: 80px;
  }

  .audio-player__volume--popup {
    display: none; // Hide popup at wide widths, show inline slider
  }
}

// Medium desktop - compact volume slider to prevent overlap
// At 1500px, switch from inline slider to popup volume control
@media (max-width: 1500px) {
  .audio-player__volume--inline {
    display: none !important;
  }

  .audio-player__volume--popup {
    display: flex !important;
  }

  .audio-player__actions {
    gap: 2px;
    flex-shrink: 0;
  }

  .audio-player__controls {
    gap: 0;
  }
}

// Small desktop - hide non-essential elements
@media (max-width: 1024px) {
  // Hide rating at small widths
  .audio-player__rating-wrapper {
    display: none;
  }
}

// Slide transition
.player-slide-enter-active,
.player-slide-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.player-slide-enter-from,
.player-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

// Light theme adjustments
.v-theme--light {
  .audio-player {
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  }
}

// Responsive - expanded mobile player (not mini)
@media (max-width: 768px) {
  .audio-player:not(.audio-player--mini) {
    // Compact padding for mobile
    padding: 20px 12px 8px 12px;
    height: auto;
    min-height: 100px; // More compact height

    .audio-player__content {
      flex-wrap: wrap;
      gap: 8px; // Reduced gap
    }

    .audio-player__track {
      order: 1;
      flex: 1 1 auto !important; // Override base flex: 0 0 180px
      max-width: none !important; // Override base 200px max-width
      min-width: 0;
      height: 44px; // Smaller height
      gap: 8px; // Tighter spacing

      // Target TrackInfo component's internal elements (deep selector)
      :deep(.track-info) {
        flex: 1;
        min-width: 0;
        width: 100%;
      }

      :deep(.track-info__text) {
        flex: 1;
        min-width: 0;
      }

      :deep(.track-info__title) {
        font-size: 0.8125rem;
        line-height: 1.1;
      }

      :deep(.track-info__artist) {
        font-size: 0.6875rem;
        line-height: 1.1;
      }
    }

    .audio-player__artwork {
      width: 44px; // Smaller artwork
      height: 44px;
      border-radius: 4px; // Smaller radius
    }

    .audio-player__info {
      height: 44px;
      justify-content: center;
      gap: 2px; // Tighter text spacing
    }

    .audio-player__title {
      font-size: 0.8125rem; // Slightly smaller
      line-height: 1.1;
    }

    .audio-player__artist {
      font-size: 0.6875rem; // Smaller artist text
      line-height: 1.1;
    }

    .audio-player__controls {
      order: 2;
      gap: 2px; // Tighter control spacing
      width: auto;

      .v-btn {
        min-width: 36px; // Smaller buttons
        width: 36px;
        height: 36px;
      }
    }

    .audio-player__progress {
      order: 4;
      flex-basis: 100%;
      max-width: none;
      gap: 6px; // Tighter spacing

      .audio-player__time {
        font-size: 0.6875rem; // Smaller time text
      }
    }

    .audio-player__actions {
      order: 3;
      gap: 2px; // Tighter spacing

      .v-btn {
        min-width: 36px; // Smaller buttons
        width: 36px;
        height: 36px;
      }

      .audio-player__rating-wrapper {
        margin-right: 2px;
        padding-right: 2px;
      }
    }

    // Hide volume control on mobile widths (CSS fallback for JS detection)
    // Mobile devices use hardware volume buttons, not on-screen slider
    .audio-player__volume,
    .audio-player__volume--inline,
    .audio-player__volume--popup {
      display: none !important;
    }

    // Hide expand button on mobile widths - user can tap expand chevron instead
    .audio-player__actions .v-btn[aria-label="Open full player"] {
      display: none;
    }

    // Show mobile expand chevron button at mobile widths - centered at top
    .audio-player__mobile-expand {
      display: flex;
      top: -14px;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }

    // Show mobile close button at top right
    .audio-player__mobile-close-btn {
      display: flex !important;
      position: absolute !important;
      top: 8px;
      right: 8px;
      z-index: 10;
    }

    // Hide desktop close button at mobile widths
    .audio-player__desktop-close {
      display: none !important;
    }

    .audio-player__mobile-header {
      top: 6px; // Slightly lower for more compact look
      padding: 0 12px;
    }
  }
}

// Very narrow screens (iPhone SE, small phones) - stacked layout with title on top
@media (max-width: 480px) {
  .audio-player:not(.audio-player--mini) {
    padding: 8px 12px;
    min-height: auto;
    height: auto;

    .audio-player__content {
      flex-direction: column;
      gap: 2px;
      align-items: stretch;
    }

    // Row 1: Artwork left, title/artist right
    .audio-player__track {
      order: 1;
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      max-width: none !important; // Override base 200px max-width
      flex: 1 1 auto !important; // Override base flex: 0 0 180px
      height: auto;
      gap: 10px;

      // Target TrackInfo component's internal elements (deep selector)
      :deep(.track-info) {
        flex: 1;
        min-width: 0;
        width: 100%;
      }

      :deep(.track-info__text) {
        flex: 1;
        min-width: 0;
      }

      :deep(.track-info__title) {
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.3;
        font-size: 0.9375rem;
        font-weight: 600;
      }

      :deep(.track-info__artist) {
        font-size: 0.75rem;
      }
    }

    .audio-player__artwork {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      flex-shrink: 0;
    }

    .audio-player__info {
      flex: 1;
      min-width: 0;
      height: auto;
      justify-content: center;
      gap: 4px;
    }

    .audio-player__title {
      font-size: 0.9375rem;
      font-weight: 600;
      -webkit-line-clamp: 2;
    }

    .audio-player__artist {
      font-size: 0.75rem;
    }

    // Row 2: Controls centered
    .audio-player__controls {
      order: 2;
      justify-content: center;
      width: 100%;
      gap: 4px;

      .v-btn {
        min-width: 36px;
        width: 36px;
        height: 36px;
      }
    }

    // Show actions row with queue button on narrow screens
    .audio-player__actions {
      order: 3;
      justify-content: center;
      width: 100%;
      gap: 4px;

      .v-btn {
        min-width: 32px;
        width: 32px;
        height: 32px;
      }
    }

    // Progress bar below controls
    .audio-player__progress {
      order: 4;
      width: 100%;
      flex-basis: 100%;
    }

    // Explicitly hide volume controls at mobile widths
    .audio-player__volume,
    .audio-player__volume--inline,
    .audio-player__volume--popup {
      display: none !important;
    }
  }
}

// Rating countdown timer
.rating-countdown {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  transition: all 0.3s ease;

  .countdown-icon {
    animation: hourglass-rotate 2s ease-in-out infinite;
  }

  .countdown-text {
    font-variant-numeric: tabular-nums;
    min-width: 28px;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

@keyframes hourglass-rotate {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
}

// Unlock animation
.rating-unlocked-animation {
  animation: rating-unlock 1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes rating-unlock {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
